import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { SignIn } from './SignIn';
import { BrandMark } from './BrandMark';
import { Workspace } from './Workspace';
import { CapacityChip, useCapacity } from './Capacity';
import { api } from './api';
import { tip, tipAt } from './tips';
import { QueueActionsProvider } from './queueActions';
import { QueueHints, QueueTour, ShortcutOverlay } from './QueueHints';
import { useAutoAdvancePreference } from './useQueueKeymap';
import { usePopover } from './usePopover';
import { useSession, type SessionUser } from './session';
import { AreaToolbar, PanelLoading, Stage } from './Surface';
import { can, type Capability } from '@actos/shared';

/*
 * Areas load when they are first opened — plan §4.1.
 *
 * Every area used to be a static import, so a rep who only ever opens SDR still
 * downloaded and parsed Library, Admin, the account drawer and the blocklist
 * before the first paint. Splitting them moves that cost to the click that
 * needs it.
 *
 * `Workspace` deliberately stays static: SDR is the default area for everyone
 * who has it, so its chunk would be requested immediately on every load — a
 * second round trip in front of the first screen, buying nothing. `SignIn`,
 * `BrandMark` and `CapacityChip` stay for the same reason.
 *
 * The `.then` unwrapping is because `lazy` wants a default export and these are
 * all named.
 */
const Settings = lazy(() => import('./Settings').then((m) => ({ default: m.Settings })));
const Admin = lazy(() => import('./Admin').then((m) => ({ default: m.Admin })));
const Library = lazy(() => import('./Library').then((m) => ({ default: m.Library })));
const Contacts = lazy(() => import('./Contacts').then((m) => ({ default: m.Contacts })));
const Accounts = lazy(() => import('./Accounts').then((m) => ({ default: m.Accounts })));
const Suppressions = lazy(() =>
  import('./Suppressions').then((m) => ({ default: m.Suppressions })),
);
const Replies = lazy(() => import('./Replies').then((m) => ({ default: m.Replies })));
const TeamMetrics = lazy(() =>
  import('./TeamMetrics').then((m) => ({ default: m.TeamMetrics })),
);
const EnrollWizard = lazy(() => import('./Enroll').then((m) => ({ default: m.EnrollWizard })));
const Marketing = lazy(() => import('./Marketing').then((m) => ({ default: m.Marketing })));

/*
 * The area placeholder is now the SHAPE of an area, not a sentence.
 *
 * `<p>Loading…</p>` is one line tall standing in for a whole page, so opening
 * a tab for the first time went full page → one line → full page. That is the
 * lazy chunk arriving, and it looked like the app collapsing.
 *
 * `table`, because every lazily-loaded area here is a list or a console. The
 * two form-measure areas, Settings and Admin, are also the two nobody opens in
 * a hurry, and being 36rem too wide for a third of a second is a smaller lie
 * than being one line tall.
 */
function AreaLoading() {
  return <PanelLoading measure="table" />;
}

/**
 * The five top-level areas — plan §16.0.
 *
 * SDR and Marketing are the mocked work surfaces (§15). Library, Settings and
 * Admin are specified in §16; the shared surfaces of §17 are reached from
 * within them.
 *
 * Each area declares the capability that gates it, so the nav is derived from
 * the same role matrix the API enforces rather than a second hand-maintained
 * list that can drift out of agreement with it.
 */
const AREAS = [
  { key: 'sdr', label: 'SDR', capability: 'workspace.sdr.own' },
  { key: 'marketing', label: 'Marketing', capability: 'workspace.marketing' },
  { key: 'accounts', label: 'Accounts', capability: 'workspace.sdr.own' },
  { key: 'contacts', label: 'Contacts', capability: 'workspace.sdr.own' },
  // Every role can SEE the blocklist — a rep needs to know why a contact
  // cannot be emailed. Editing and importing are manager+ and gated in the API.
  { key: 'suppression', label: 'Blocklist', capability: 'suppression.view' },
  { key: 'library', label: 'Library', capability: 'library.draft' },
  { key: 'settings', label: 'Settings', capability: 'settings.own' },
  { key: 'admin', label: 'Admin', capability: 'admin.users' },
] as const satisfies readonly { key: string; label: string; capability: Capability }[];

type AreaKey = (typeof AREAS)[number]['key'];

/**
 * What each area is for, said to someone who has not read the plan. Kept beside
 * AREAS rather than inside it so the nav's shape — and the capability gate
 * derived from it — stays exactly as it was.
 */
const NAV_TIPS: Record<AreaKey, string> = {
  sdr: 'Your own queue of drafts waiting for approval, and the replies they brought back. Nothing sends until you approve it.',
  marketing:
    'The content campaigns are assembled from — blocks, templates, themes, articles, segments — and, once the campaign spine lands, the campaigns themselves.',
  accounts: 'The companies you work: their contacts, their signals, and the mailbox pinned to each. Looking changes nothing.',
  contacts: 'People across every account, with their consent state and whether the record is authoritative or still staged.',
  suppression: 'Everyone this environment refuses to email, and why. Anyone can look; only managers and admins can add or import.',
  library: 'Sequences, blocks and other reusable copy. Anyone can draft here; only an admin can approve a version for use.',
  settings: 'Your own profile and inbox connections. Nothing you change here affects anyone else in the environment.',
  admin: 'Tenants, domains, connectors, caps and policy — the settings that change what everyone sends. Admins only.',
};

export function App() {
  const { state, signIn, signOut } = useSession();
  // SDR is the default: the work surface, not the console. An admin who wants
  // the console clicks once; a rep who only ever wants the queue clicks never.
  // `?area=` is the one piece of query-state the app carries (Block 3.1): a
  // campaign must be linkable from a notice, and the app has no router.
  const [area, setArea] = useState<AreaKey>(() => {
    const requested = new URLSearchParams(window.location.search).get('area');
    return AREAS.some((a) => a.key === requested) ? (requested as AreaKey) : 'sdr';
  });
  const [initialCampaignId] = useState<number | null>(() => {
    const id = Number(new URLSearchParams(window.location.search).get('campaign'));
    return Number.isInteger(id) && id > 0 ? id : null;
  });

  if (state.status === 'loading') {
    return (
      <div className="signin">
        <p className="why">Checking your session&hellip;</p>
      </div>
    );
  }

  if (state.status === 'anonymous') {
    return <SignIn onSignIn={signIn} />;
  }

  const { user } = state;
  const visible = AREAS.filter((a) => can(user.role, a.capability));
  const current = visible.some((a) => a.key === area) ? area : visible[0]!.key;
  const home = visible.find((a) => a.key === 'sdr')?.key ?? visible[0]!.key;
  const homeLabel = visible.find((a) => a.key === home)!.label;

  return (
    <div className="app">
      <header className="topbar">
        {/*
          The wordmark is the app's home affordance — SDR is what "home" means
          here (it is the default area at mount, and the work surface everything
          else supports). `home` is derived from `visible` rather than hardcoded
          to 'sdr': a role without `workspace.sdr.own` has no SDR tab to land on,
          and sending them to an area their capability set does not include
          would bounce them straight back out via the `current` fallback below.
        */}
        <button type="button" className="brand brand-home" aria-label={`Vector ActOS — go to ${homeLabel}`} {...tip(`Returns you to ${homeLabel}, the home surface. Nothing in progress is lost — drafts keep their edits and the queue keeps its place.`)} onClick={() => setArea(home)}>
          <BrandMark />
          <span>Vector ActOS</span>
        </button>

        <nav className="nav">
          {/*
            The last tab is the right-most thing in a bar the app clips
            (`.app` is `overflow: hidden`), so its bubble grows leftwards or
            loses its end. Every other tab has room to its right.
          */}
          {visible.map((a, i) => (
            <button
              key={a.key}
              type="button"
              aria-current={current === a.key}
              {...(i === visible.length - 1
                ? tipAt(NAV_TIPS[a.key], 'end')
                : tip(NAV_TIPS[a.key]))}
              onClick={() => setArea(a.key)}
            >
              {a.label}
            </button>
          ))}
        </nav>

        <div className="topbar-right">
          <NoticeBell />
          <UserChip user={user} onSignOut={signOut} />
        </div>
      </header>

      {/*
        The SDR workspace manages its own scrolling — two independent panes
        under the fixed header (§15.1) — so it fills the region instead of
        sitting on a scrolling page. Every other surface scrolls.

        Through `Stage` rather than a ternary on the class string, so the mode
        is a property of the CONTENT and a second area that wants to fill does
        not have to be added to a condition on the area's name.
      */}
      <Stage variant={current === 'sdr' ? 'fill' : 'scroll'}>
        <Suspense fallback={<AreaLoading />}>
          {current === 'sdr' && <Sdr role={user.role} />}
          {current === 'accounts' && <Accounts />}
          {current === 'contacts' && <Contacts />}
          {current === 'suppression' && <Suppressions />}
          {current === 'library' && <Library />}
          {current === 'admin' && <Admin />}
          {current === 'settings' && <Settings userEmail={user.email} role={user.role} />}
          {current === 'marketing' && <Marketing role={user.role} initialCampaignId={initialCampaignId} />}
        </Suspense>
      </Stage>
    </div>
  );
}

interface Notice {
  id: number;
  kind: string;
  severity: 'info' | 'warn' | 'critical';
  title: string;
  body: string | null;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  readAt: string | null;
}

/** Where a notice about an entity opens — the campaign for a campaign notice (Block 3.1's query-state); nothing else is linkable yet. */
function noticeHref(n: Notice): string | null {
  return n.entityType === 'campaign' && n.entityId ? `/?area=marketing&campaign=${encodeURIComponent(n.entityId)}` : null;
}

/**
 * The in-app notices centre — plan §16.4.
 *
 * Admins already have a Slack feed and the Health page's own list; everyone
 * else had no in-app surface at all, which broke "every notice exists in-app
 * first" (R22) for anyone who isn't an admin. `GET /notices` filters server-
 * side to what this caller's role and identity may see — a rep gets a guard
 * failure on their own task or an account-request outcome, never a domain
 * quarantine that isn't theirs to act on.
 */
function NoticeBell() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [unread, setUnread] = useState(0);
  // Click-away and Escape dismissal: the bell used to close only by pressing it
  // again, so the menu sat over the work surface until you found it.
  const { ref, open, toggle } = usePopover<HTMLDivElement>();

  const load = useCallback(() => {
    void api<{ notices: Notice[]; unread: number }>('/notices')
      .then((data) => {
        setNotices(data.notices);
        setUnread(data.unread);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
    // Polling, same as the workspace board (§15.1) — no push channel exists,
    // and a stale badge is a worse failure mode than a 60-second lag.
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const onToggle = () => {
    const opening = !open;
    toggle();
    if (opening && unread > 0) {
      // Opening the bell is "I looked" — mark everything currently visible as
      // read without waiting for each one to be dismissed individually.
      void api('/notices/read-all', { method: 'POST' })
        .then(() => setUnread(0))
        .catch(() => undefined);
    }
  };

  const dismiss = (id: number) => {
    setNotices((ns) => ns.filter((n) => n.id !== id));
    void api(`/notices/${id}/dismiss`, { method: 'POST' }).catch(() => load());
  };

  return (
    <div className="notice-bell" ref={ref}>
      <button
        type="button"
        className="notice-btn"
        {...tipAt(
          'Alerts about your accounts and mailboxes — throttled sends, blocked guards, routing requests. Opening this marks them read; dismissing removes one for good.',
          'end',
        )}
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={unread > 0 ? `${unread} unread notices` : 'Notices'}
      >
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
          <path
            d="M10 2.5c-.9 0-1.6.7-1.6 1.6v.4c-2.2.5-3.8 2.5-3.8 4.9v2.9L3 14.8v.8h14v-.8l-1.6-2.5V9.4c0-2.4-1.6-4.4-3.8-4.9v-.4c0-.9-.7-1.6-1.6-1.6z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
          <path
            d="M8 16.2a2 2 0 0 0 4 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
        {unread > 0 && <span className="notice-count">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="notice-menu">
          <p className="user-menu-name">Notices</p>
          {notices.length === 0 && <p className="fine">Nothing outstanding.</p>}
          <ul className="notice-list">
            {notices.map((n) => (
              <li key={n.id} className={`notice-item notice-${n.severity}`}>
                <div>
                  <strong>{n.title}</strong>
                  {n.body && <p className="fine">{n.body}</p>}
                  <p className="fine">
                    {new Date(n.createdAt).toLocaleString()}
                    {noticeHref(n) && (
                      <>
                        {' · '}
                        <a href={noticeHref(n)!}>Open the campaign</a>
                      </>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  className="link-btn"
                  {...tip('Removes this notice for everyone who could see it. It stays gone even after the condition recurs — a fresh occurrence raises a new one.')}
                  onClick={() => dismiss(n.id)}
                >
                  Dismiss
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UserChip({ user, onSignOut }: { user: SessionUser; onSignOut: () => void }) {
  // Click-away and Escape dismissal — see NoticeBell.
  const { ref, open, toggle } = usePopover<HTMLDivElement>();

  return (
    <div className="user-chip" ref={ref}>
      <button
        type="button"
        className="user-btn"
        {...tipAt(
          'Shows which account you are signed in as, the role you hold, and the way to sign out. Opening it changes nothing.',
          'end',
        )}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="avatar">{(user.fullName ?? user.email).charAt(0).toUpperCase()}</span>
        <span className="user-meta">
          <span className="user-email">{user.email}</span>
          {/* Role is shown, not hidden: §16.0's gate is only comprehensible if
              you can see which side of it you are on. */}
          <span className="user-role">{user.role}</span>
        </span>
      </button>

      {open && (
        <div className="user-menu">
          <p className="user-menu-name">{user.fullName ?? user.email}</p>
          <p className="fine">
            Signed in with Google &middot; {user.role}
          </p>
          <button
            type="button"
            className="signout-btn"
            {...tipAt(
              'Ends your session and returns you to the sign-in screen. Anything you already approved stays queued and still sends.',
              'end',
            )}
            onClick={onSignOut}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The SDR area: the queue, plus the way to fill it.
 *
 * Enrollment lives here rather than in its own top-level area because it is the
 * same job — a rep filling their own pipeline. `reloadKey` forces the queue to
 * refetch after an enrollment, so the new steps appear without a page reload.
 */
function Sdr({ role }: { role: 'user' | 'manager' | 'admin' }) {
  const [reloadKey, setReloadKey] = useState(0);
  const [enrolling, setEnrolling] = useState(false);
  const [view, setView] = useState<'queue' | 'replies' | 'team'>('queue');
  const [showShortcuts, setShowShortcuts] = useState(false);
  /*
   * The auto-advance preference lives here, not in `Workspace`, so the toggle in
   * the shortcut overlay and the behaviour in the queue are one piece of state.
   * Reading the hook in both places would give two of them.
   */
  const [autoAdvance, setAutoAdvance] = useAutoAdvancePreference();
  /**
   * The badge lives on the tab, so it is fetched here rather than inside
   * Replies — a rep sitting on the Queue needs to see that something came back
   * without switching to look.
   */
  const [unreviewed, setUnreviewed] = useState(0);
  /**
   * Fetched here, used twice: the toolbar chip and the warning beside Approve
   * (R11). Polling it once rather than in each component keeps the §4 poll
   * budget where it was and guarantees the two cannot disagree about how much
   * room a mailbox has left.
   */
  const capacity = useCapacity();
  /**
   * A follow-up started from a reply — R13. Held here because the two ends are
   * in different tabs: the button is in Replies, the compose box lives on the
   * queue. Cleared once the box has taken it, so switching tabs again does not
   * reopen a compose the rep already dismissed.
   */
  const [followUp, setFollowUp] = useState<{ to: string; brief: string } | null>(null);

  const refreshBadge = useCallback(() => {
    void api<{ unreviewedCount: number }>('/replies')
      .then((data) => setUnreviewed(data.unreviewedCount))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshBadge();
  }, [refreshBadge]);

  return (
    <div className="sdr-area">
      {/*
        `AreaToolbar` reserves one control's height whatever is in it, which is
        what stops the queue/replies switch from moving everything under it by
        six pixels. The subnav is its LEFT child — these bubbles have room to
        their right and take a plain `tip()`.
      */}
      <AreaToolbar
        left={
          <nav className="subnav">
            <button
              type="button"
              className={view === 'queue' ? 'active' : ''}
              {...tip(
                'Shows the drafts waiting on you. Switching between the two views sends nothing and loses no work.',
              )}
              onClick={() => setView('queue')}
            >
              Queue
            </button>
            <button
              type="button"
              className={view === 'replies' ? 'active' : ''}
              {...tip(
                'Shows what ActOS did about incoming replies, so a wrong call can be corrected. The number counts the ones nobody has reviewed yet.',
              )}
              onClick={() => setView('replies')}
            >
              Replies
              {unreviewed > 0 && <span className="badge">{unreviewed}</span>}
            </button>
            {/*
              Manager and above only, the same shape Accounts uses for its
              Unrouted tab. Gated on the capability rather than the role name, so
              it follows the matrix rather than a second copy of it.
            */}
            {can(role, 'workspace.sdr.team') && (
              <button
                type="button"
                className={view === 'team' ? 'active' : ''}
                {...tip(
                  'How often drafts are approved as written, edited first, or thrown away — figures about the writer, not about the reps. Reading only.',
                )}
                onClick={() => setView('team')}
              >
                Team
              </button>
            )}
            </nav>
        }
        /* The right-hand slot: no room out there, so these do take
           `tipAt(…, 'end')`. */
        right={
          <>
            <CapacityChip data={capacity} />
            {view === 'queue' && (
              <button
                type="button"
                {...tipAt(
                  'Opens the enrollment wizard: pick accounts, pick their people, pick a cadence. Nothing is enrolled until the final step.',
                  'end',
                )}
                onClick={() => setEnrolling(true)}
              >
                Enroll
              </button>
            )}
          </>
        }
      />

      {view === 'queue' ? (
        /* The provider covers the queue AND its hint footer, because the footer
           shows whatever the mounted draft pane published. It renders no DOM, so
           both stay direct flex children of `.sdr-area`. */
        <QueueActionsProvider>
          {/* A modal, not a strip: the queue keeps its place and its scroll,
              and the wizard floats above it.

              Its own Suspense boundary, so fetching the wizard's chunk does not
              hand the whole SDR area to the outer fallback — the queue behind it
              would blank out and lose its scroll on the way to a modal. */}
          {enrolling && (
            <Suspense fallback={null}>
              <EnrollWizard
                onClose={() => setEnrolling(false)}
                onEnrolled={() => setReloadKey((k) => k + 1)}
              />
            </Suspense>
          )}
          {/* Above the panes and below the tab bar: the first thing read on a
              first visit, and out of the way of everything it describes. */}
          <QueueTour onShowHelp={() => setShowShortcuts(true)} />
          <Workspace
            key={reloadKey}
            role={role}
            autoAdvance={autoAdvance}
            onShowHelp={() => setShowShortcuts(true)}
            unreviewedReplies={unreviewed}
            onOpenReplies={() => setView('replies')}
            capacity={capacity}
            followUp={followUp}
            onFollowUpConsumed={() => setFollowUp(null)}
          />
          {/* Last child of the flex column, so it RESERVES its height rather
              than floating over the panes: the draft pane's Approve bar is
              `position: sticky; bottom: 0`, and a fixed footer would cover it
              at short window heights. */}
          <QueueHints onShowHelp={() => setShowShortcuts(true)} />
        </QueueActionsProvider>
      ) : (
        /* Replies takes the queue's geometry, not the scrolling stage's: same
           gutters, same top offset, a rail that fills the pane and scrolls
           itself. It used to keep "the old page-scroll feel" here — a 60rem
           `.panel`, the Settings/Admin form measure, on a page-scrolling wrapper
           — which made the tab with the WIDER content the narrower of the two and
           let the card stop wherever the list stopped, so a nearly-empty triage
           left bare ground under a short card while Queue's pane ran to the
           bottom. Switching tabs moved the content sideways and up. §15.1 puts
           list surfaces at the 96rem measure; `.replies-pane` applies it.

           Its own boundary still keeps the tab bar on screen while the chunk
           arrives — and the fallback wears `.panel` too, so the rail does not
           flash a different shape on the way in. */
        <div className="replies-pane">
          <Suspense fallback={<PanelLoading measure="table" />}>
            {view === 'replies' ? (
              <Replies
                onReviewed={refreshBadge}
                onDraftFollowUp={(seed) => {
                  setFollowUp(seed);
                  setView('queue');
                }}
              />
            ) : (
              <TeamMetrics />
            )}
          </Suspense>
        </div>
      )}

      {showShortcuts && (
        <ShortcutOverlay
          autoAdvance={autoAdvance}
          onAutoAdvanceChange={setAutoAdvance}
          onClose={() => setShowShortcuts(false)}
        />
      )}
    </div>
  );
}


